// IndexedDB queue for audio recordings awaiting upload.
// The queue is the *source of truth* until the server acknowledges a recording
// (HTTP 201). iOS has no Background Sync so retries fire on: `online`,
// `visibilitychange` (visible), and app mount.
//
// Schema v1:
//   pending_audio: { id, blob, mimeType, sizeBytes, visitId, createdAt,
//                    uploadState, attempts, lastError?, nextAttemptAt? }

import { openDB, type DBSchema, type IDBPDatabase } from 'idb'

export type PendingAudioState =
  | 'queued'      // waiting for first attempt
  | 'uploading'   // in flight — do not retry
  | 'failed'      // transient failure — will retry via orchestrator
  | 'done'        // server ack'd — safe to purge (kept briefly for debugging)

export type PendingAudio = {
  id: string
  blob: Blob
  mimeType: string
  sizeBytes: number
  visitId: number
  createdAt: number
  uploadState: PendingAudioState
  attempts: number
  lastError?: string
  /** Server-assigned audio id once uploaded. */
  audioId?: number
  /** Epoch ms — orchestrator skips entries until now >= nextAttemptAt. */
  nextAttemptAt?: number
}

interface VoiceSoapDB extends DBSchema {
  pending_audio: {
    key: string
    value: PendingAudio
    indexes: {
      by_state: PendingAudioState
      by_visit: number
    }
  }
}

const DB_NAME = 'voicesoap'
const DB_VERSION = 1
const STORE = 'pending_audio'

let dbPromise: Promise<IDBPDatabase<VoiceSoapDB>> | null = null

export function getQueueDb(): Promise<IDBPDatabase<VoiceSoapDB>> {
  if (!dbPromise) {
    dbPromise = openDB<VoiceSoapDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' })
        store.createIndex('by_state', 'uploadState')
        store.createIndex('by_visit', 'visitId')
      },
      blocked() {
        // Another tab has the DB open at an older version — user will see a
        // stale state toast from the UI layer; nothing we can do here.
      },
      terminated() {
        dbPromise = null
      },
    })
  }
  return dbPromise
}

function genId(): string {
  // Non-cryptographic id; only needs to be unique per client session.
  return `rec_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
}

export async function enqueueAudio(params: {
  blob: Blob
  mimeType: string
  visitId: number
}): Promise<PendingAudio> {
  const db = await getQueueDb()
  const entry: PendingAudio = {
    id: genId(),
    blob: params.blob,
    mimeType: params.mimeType,
    sizeBytes: params.blob.size,
    visitId: params.visitId,
    createdAt: Date.now(),
    uploadState: 'queued',
    attempts: 0,
  }
  await db.put(STORE, entry)
  return entry
}

export async function getPending(id: string): Promise<PendingAudio | undefined> {
  const db = await getQueueDb()
  return db.get(STORE, id)
}

export async function listByState(state: PendingAudioState): Promise<PendingAudio[]> {
  const db = await getQueueDb()
  return db.getAllFromIndex(STORE, 'by_state', state)
}

export async function listByVisit(visitId: number): Promise<PendingAudio[]> {
  const db = await getQueueDb()
  return db.getAllFromIndex(STORE, 'by_visit', visitId)
}

export async function listRetryable(now = Date.now()): Promise<PendingAudio[]> {
  const queued = await listByState('queued')
  const failed = await listByState('failed')
  return [...queued, ...failed].filter(
    (e) => e.nextAttemptAt == null || e.nextAttemptAt <= now,
  )
}

export async function markUploading(id: string): Promise<PendingAudio | undefined> {
  const db = await getQueueDb()
  const tx = db.transaction(STORE, 'readwrite')
  const cur = await tx.store.get(id)
  if (!cur) {
    await tx.done
    return undefined
  }
  const next: PendingAudio = { ...cur, uploadState: 'uploading', attempts: cur.attempts + 1 }
  await tx.store.put(next)
  await tx.done
  return next
}

export async function markDone(id: string, audioId: number): Promise<void> {
  const db = await getQueueDb()
  const cur = await db.get(STORE, id)
  if (!cur) return
  await db.put(STORE, { ...cur, uploadState: 'done', audioId, lastError: undefined })
}

export async function markFailed(
  id: string,
  error: string,
  backoffMs: number,
): Promise<void> {
  const db = await getQueueDb()
  const cur = await db.get(STORE, id)
  if (!cur) return
  await db.put(STORE, {
    ...cur,
    uploadState: 'failed',
    lastError: error,
    nextAttemptAt: Date.now() + backoffMs,
  })
}

export async function removePending(id: string): Promise<void> {
  const db = await getQueueDb()
  await db.delete(STORE, id)
}

/** Purge `done` entries older than `olderThanMs` (default 1h). */
export async function purgeDone(olderThanMs = 60 * 60 * 1000): Promise<number> {
  const db = await getQueueDb()
  const entries = await db.getAllFromIndex(STORE, 'by_state', 'done')
  const cutoff = Date.now() - olderThanMs
  let removed = 0
  for (const e of entries) {
    if (e.createdAt < cutoff) {
      await db.delete(STORE, e.id)
      removed++
    }
  }
  return removed
}

/** Exponential backoff with jitter: 2s, 4s, 8s, 16s, capped at 60s. */
export function computeBackoffMs(attempts: number): number {
  const base = Math.min(60_000, 2_000 * 2 ** Math.max(0, attempts - 1))
  const jitter = base * 0.25 * Math.random()
  return Math.floor(base + jitter)
}
